import React from 'react';
import type { ReactNode, CSSProperties } from 'react';
import useStyles from '../../theme/useStyles';
import { useReadOnly } from '../../utils/readOnly';
import Icon, { iconify, iconFor } from './Icon';

// Module title, then the module's tabs as an underline bar. Every module's
// top of page.
//
//   <PageHeader
//     icon="⛪" title="Event Management"
//     tabs={[{ key: 'directory', label: 'Event Calendar' }, { key: 'form', label: '+ Schedule New Event' }]}
//     activeTab={view} onTabChange={setView}
//   />
//
// Title on the left and any `actions` on the right make the first row; the
// tabs run along a rule beneath, the open one marked by a coloured underline
// in the module's accent. Filled-pill tabs used to sit beside the title and
// wrapped unpredictably as soon as a module had more than four.
export interface PageHeaderTab {
  key: string;
  label: ReactNode;
  /** 'danger' renders the tab in red — Deleted / Voided lists. */
  variant?: 'danger';
  /** A create/edit tab (+ Record New Entry, + Add) — hidden for a read-only user. */
  write?: boolean;
}

export interface PageHeaderProps {
  icon?: ReactNode;
  title?: ReactNode;
  /** Replaces the icon+title block entirely — e.g. a tab switch that IS the header. */
  leading?: ReactNode;
  tabs?: PageHeaderTab[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
  /** Extra controls rendered on the right of the title row. */
  actions?: ReactNode;
  isDarkMode?: boolean;
}

const PageHeader = ({
  icon,
  title,
  leading,
  tabs = [],
  activeTab,
  onTabChange,
  actions,
  isDarkMode
}: PageHeaderProps) => {
  const s = useStyles(isDarkMode);
  const t = s.t;
  const readOnly = useReadOnly();

  // A read-only user can browse but not create or edit, so the create/edit tab
  // (+ Record New Entry, + Add) is dropped — it only opens a form they can't save.
  const visibleTabs = tabs.filter((tab) => !(readOnly && tab.write));

  const tabStyle = (tab: PageHeaderTab): CSSProperties => {
    const active = activeTab === tab.key;
    const colour = tab.variant === 'danger' ? t.danger : t.primaryText;
    return {
      background: 'none',
      border: 'none',
      borderBottom: `2px solid ${active ? colour : 'transparent'}`,
      marginBottom: '-1px',
      padding: '10px 14px',
      cursor: 'pointer',
      color: active ? colour : (tab.variant === 'danger' ? t.danger : t.textMuted),
      fontWeight: active ? t.fontWeight.bold : t.fontWeight.medium,
      fontSize: t.fontSize.md,
      whiteSpace: 'nowrap',
      lineHeight: 1.3
    };
  };

  return (
    <div style={{ marginBottom: t.spacing.lg }}>
      <div style={{ ...s.headerRow, marginBottom: visibleTabs.length ? t.spacing.xs : 0 }}>
        {leading ?? (
          <h2 style={{ ...s.headerTitle, display: 'flex', alignItems: 'center', gap: '10px' }}>
            {icon && (
              <span className="notranslate" translate="no" style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '36px', height: '36px', borderRadius: t.radius.md,
                backgroundColor: t.bgAccent, color: t.primaryText, fontSize: '18px'
              }}>{typeof icon === 'string' ? <Icon name={iconFor(icon)} size={20} /> : icon}</span>
            )}
            {title}
          </h2>
        )}
        {actions && (
          <div style={{ display: 'flex', alignItems: 'center', gap: t.spacing.sm, flexWrap: 'wrap' }}>
            {actions}
          </div>
        )}
      </div>
      {visibleTabs.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '4px',
          borderBottom: `1px solid ${t.border}`
        }}>
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              aria-pressed={activeTab === tab.key}
              onClick={() => onTabChange?.(tab.key)}
              style={tabStyle(tab)}
            >
              {iconify(tab.label)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
